<template>
  <v-dialog
    max-width="400px"
    :fullscreen="mobile"
    transition="dialog-top-transition"
    persistent
    :model-value="modelValue"
  >
    <v-card>
      <v-card-title class="bg-primary d-flex align-center">
        <span class="headline text-uppercase">{{
          $t('donationDialog.title')
        }}</span>
        <v-spacer></v-spacer>
        <v-btn icon variant="text" color="white" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-form validate-on="blur" @submit.prevent="submitPayment">
        <v-window v-model="step" disabled>
          <v-window-item :value="1">
            <v-card-text>
              <!-- ... autres parties du template ... -->
              <div class="text-caption">
                <v-icon size="14px" class="mr-1">mdi-lock</v-icon>
                <span class="font-weight-bold">{{
                  $t('donationDialog.securityNote')
                }}</span>
              </div>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="2">
            <v-card-text
              class="text-primary pt-3 d-flex flex-column align-center"
            >
              <v-icon size="50px" color="secondary">mdi-heart-circle</v-icon>
              <div class="text-center mt-3 font-weight-medium">
                <p>{{ $t('donationDialog.thankYouTitle') }}</p>
                <p>{{ $t('donationDialog.thankYouMessage') }}</p>
              </div>
            </v-card-text>
          </v-window-item>
        </v-window>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="$emit('close')">{{
            step === 1
              ? $t('donationDialog.cancelButton')
              : $t('donationDialog.closeButton')
          }}</v-btn>
          <v-btn
            v-if="step === 1"
            color="secondary"
            :loading="submitting"
            type="submit"
            text
          >
            {{ $t('donationDialog.donateButton') }} {{ form.amount }} €
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useDisplay, useTheme } from 'vuetify'
import { useStripe } from '@/shared/payment'
import { useStorage } from '@vueuse/core'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const { mobile } = useDisplay()

const form = ref({
  email: '',
  amount: 10,
})
const amounts = [2, 5, 10, 20, 50, 100, 200, 500, 1000]
const submitting = ref(false)
const step = ref(1)

const {
  stripe,
  stripeError,
  stripeCard,
  stripeCardExpiry,
  stripeCardCvc,
  initStripe,
} = useStripe(useTheme())

const emailRules = [
  v => !!v || "L'e-mail est requis",
  v => /.+@.+/.test(v) || "L'e-mail doit être valide",
]

const submitPayment = async event => {
  stripeError.value = ''
  const validation = await event
  if (!validation.valid) {
    return
  }
  if (stripeError.value.length) {
    return
  }
  if (!stripeCard.value._complete) {
    stripeError.value = 'Veuillez remplir les champs de la carte bancaire.'
    return
  }
  submitting.value = true
  const finalAmount = form.value.amount + '00'

  await stripe.value
    .createPaymentMethod({
      type: 'card',
      card: stripeCard.value,
      billing_details: {
        email: form.value.email,
      },
    })
    .then(result => {
      if (result.error) {
        stripeError.value = result.error.message
        submitting.value = false
      } else {
        createSinglePayment({
          paymentMethodId: result.paymentMethod.id,
          finalAmount,
        })
      }
    })
    .catch(() => {
      submitting.value = false
    })
}

const refSlug = useStorage('ref-slug', null)

const createSinglePayment = ({ paymentMethodId, finalAmount }) => {
  fetch(`${import.meta.env.VITE_API_URL}/payment/create-payment-intent`, {
    method: 'POST',
    body: JSON.stringify({
      paymentMethodId,
      amount: finalAmount,
      email: form.value.email,
      referrerSlug: refSlug.value,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(result => {
      return result.json()
    })
    .then(data => {
      if (data.error) {
        stripeError.value = data.error
        return true
      } else if (data.status === 'succeeded') {
        step.value = 2
        return true
      }
      stripe.value
        .confirmCardPayment(data.clientSecret, {
          payment_method: {
            card: stripeCard.value,
            billing_details: {
              email: form.value.email,
            },
          },
        })
        .then(result => {
          submitting.value = false
          if (result.error) {
            stripeError.value = result.error
          } else {
            step.value = 2
          }
        })
    })
    .then(result => {
      if (result) {
        submitting.value = false
      }
    })
    .catch(() => {
      submitting.value = false
      stripeError.value =
        'Une erreur est survenue. Veuillez réessayer plus tard.'
    })
}

const fieldFocus = field => {
  setTimeout(() => {
    field.focus()
  }, 10)
}

watch(
  () => props.modelValue,
  value => {
    if (value) {
      initStripe()
    }
  },
)
</script>

<style scoped lang="scss"></style>
